/**
 * What a plan is made of, in one place for the plans list and the plan editor:
 * the product modules (a switch, the limits it governs, its sub-features), the
 * always-included catalog, the add-ons, and the admin slider ranges.
 *
 * Labels are not here: they are translation keys, resolved by `usePlanLabels`.
 */
import type { ElementType } from 'react';
import {
  BarChart3,
  CalendarDays,
  Image as ImageIcon,
  Inbox,
  Mail,
  Network,
  ScanSearch,
  SlidersHorizontal,
  Sofa,
  Gauge,
} from 'lucide-react';

import type { PlanFeatures, PlanLimits, VendorPlan } from '../our-vendors/models/vendor.model';
import { classifyPlan } from '../../../pages/vendor/subscription/plan-cta.util';
import { formatCurrency } from '../../../components/design-system/admin/format';

export type FeatureKey = keyof Omit<PlanFeatures, 'visualizationQuality'>;
export type LimitKey = keyof PlanLimits;
export type Quality = PlanFeatures['visualizationQuality'];

export const QUALITIES: Quality[] = ['low', 'medium', 'high'];

/** A product module: its switch, the usage limits it governs and its sub-features. */
export interface PlanModule {
  id: string;
  /** The feature that switches it on. None for the catalog, which every plan includes. */
  feature?: FeatureKey;
  Icon: ElementType;
  limits: LimitKey[];
  extras: FeatureKey[];
  /** Carries the visualization-quality tier. */
  quality?: boolean;
}

/** Same grouping as the vendor upgrade page and the quote configurator. */
export const PLAN_MODULES: PlanModule[] = [
  { id: 'contentStudio', feature: 'contentStudio', Icon: ImageIcon, limits: ['monthlyCredits'], extras: ['seamlessGeneration'] },
  {
    id: 'aiSpaceAnalysis',
    feature: 'aiSpaceAnalysis',
    Icon: Sofa,
    limits: ['maxVisualizationsPerMonth'],
    extras: ['model3dGeneration', 'shopperChat'],
    quality: true,
  },
  { id: 'socialScheduler', feature: 'socialScheduler', Icon: CalendarDays, limits: ['maxSocialAccounts', 'maxScheduledPostsPerMonth'], extras: ['adsManager'] },
  { id: 'emailAutomation', feature: 'emailAutomation', Icon: Mail, limits: ['maxEmailsPerMonth', 'maxAutomationRules', 'maxLeads'], extras: [] },
];

/** Catalog & team: always included, only limits. */
export const CATALOG_MODULE: PlanModule = {
  id: 'catalog',
  Icon: Network,
  limits: ['maxProducts', 'maxSeats', 'maxSpaces', 'maxImportProducts', 'maxSyncFlowsPerDay'],
  extras: [],
};

/**
 * The Free plan's depth limits: monthly meters on the paths that cost money,
 * and how many rows a limited list shows. Every plan has them; -1 (or absent)
 * is unlimited, which is what Pro and Enterprise carry.
 */
export const DEPTH_MODULE: PlanModule = {
  id: 'depth',
  Icon: Gauge,
  limits: ['maxRoomAnalysesPerMonth', 'maxShopperChatsPerMonth', 'maxSeamlessPerMonth', 'max3dModelsPerMonth', 'maxBrandReadsPerMonth', 'maxMarketRefreshesPerMonth', 'maxKeywordRows', 'maxCompetitorRows', 'maxGapRows', 'maxConversationsVisible', 'analyticsHistoryDays'],
  extras: [],
};

/** The modules, then the catalog, then the depth limits: the order both pages list them in. */
export const PLAN_GROUPS: PlanModule[] = [...PLAN_MODULES, CATALOG_MODULE, DEPTH_MODULE];

/** An add-on: one feature, or a bundle switched on and off together. */
export interface PlanAddon {
  id: string;
  keys: FeatureKey[];
  Icon: ElementType;
}

export const PLAN_ADDONS: PlanAddon[] = [
  { id: 'apiIntegration', keys: ['apiIntegration'], Icon: Inbox },
  { id: 'advancedAnalytics', keys: ['advancedAnalytics'], Icon: BarChart3 },
  { id: 'catalogCustomization', keys: ['customFields', 'categoryManagement', 'productCardCustomization'], Icon: SlidersHorizontal },
  // Off by default on every plan, Enterprise included: Enterprise is the
  // fallback for vendors with no plan assigned, so switching it on there would
  // start paying to describe every demo and abandoned account.
  { id: 'aiProductEnrichment', keys: ['aiProductEnrichment'], Icon: ScanSearch },
];

/** A module is on when its feature is (the catalog always is). */
export const moduleEnabled = (features: Partial<PlanFeatures> | undefined, m: PlanModule): boolean =>
  m.feature ? Boolean(features?.[m.feature]) : true;

/** An add-on is on when every feature in it is on. */
export const addonEnabled = (features: Partial<PlanFeatures> | undefined, addon: PlanAddon): boolean =>
  addon.keys.every((k) => Boolean(features?.[k]));

/**
 * Admin slider ranges. They start at 0 (super-admins can also flip any limit
 * to Unlimited, -1); the upper bound is only the slider's, the input takes more.
 */
export const LIMIT_RANGES: Record<LimitKey, { max: number; step: number }> = {
  monthlyCredits: { max: 100000, step: 100 },
  maxVisualizationsPerMonth: { max: 100000, step: 100 },
  maxSocialAccounts: { max: 100, step: 1 },
  maxScheduledPostsPerMonth: { max: 5000, step: 10 },
  maxEmailsPerMonth: { max: 1000000, step: 1000 },
  maxAutomationRules: { max: 500, step: 1 },
  maxLeads: { max: 200000, step: 100 },
  maxProducts: { max: 100000, step: 50 },
  maxSeats: { max: 500, step: 1 },
  maxSpaces: { max: 500, step: 1 },
  maxImportProducts: { max: 100000, step: 100 },
  maxSyncFlowsPerDay: { max: 500, step: 1 },
  maxRoomAnalysesPerMonth: { max: 100000, step: 10 },
  maxShopperChatsPerMonth: { max: 100000, step: 10 },
  maxSeamlessPerMonth: { max: 10000, step: 1 },
  max3dModelsPerMonth: { max: 10000, step: 1 },
  maxBrandReadsPerMonth: { max: 100, step: 1 },
  maxMarketRefreshesPerMonth: { max: 100, step: 1 },
  maxKeywordRows: { max: 1000, step: 5 },
  maxCompetitorRows: { max: 100, step: 1 },
  maxGapRows: { max: 1000, step: 5 },
  maxConversationsVisible: { max: 100000, step: 10 },
  analyticsHistoryDays: { max: 3650, step: 1 },
};

/**
 * How a plan's price reads: custom (sales-led) tiers have no fixed price, free
 * tiers read "Free", paid tiers show the amount; 'none' when a paid plan has no
 * price at all.
 */
export type PriceKind = 'custom' | 'free' | 'paid' | 'none';

export const priceKind = (plan: VendorPlan): PriceKind => {
  const tier = classifyPlan(plan);
  if (tier === 'sales') return 'custom';
  if (tier === 'free') return 'free';
  return plan.price === undefined || plan.price === null ? 'none' : 'paid';
};

/** "$50" / "49,99 €" in the active language; cents only when the price has them. */
export const formatPlanPrice = (plan: VendorPlan): string =>
  formatCurrency(plan.price ?? 0, plan.currency || 'usd', Number.isInteger(plan.price ?? 0) ? 0 : 2);
